import React, { useEffect, useState } from 'react';
import { Card, CardBody, Container } from "reactstrap";
import Breadcrumb from "Components/UI/Breadcrumb";
import PaginatedTable from "Components/Tables/PaginatedTable";
import { Link } from 'react-router-dom';
import useTableFilters from 'helpers/hooks/useTableFilters';
import { deleteBooking, getBookings } from 'helpers/API Calls/Operations/booking';
import Spinner from 'Components/UI/Spinner';
import convertDateFormat from 'Components/Date/DateFormat';
import DeleteModal from 'Components/Modals/DeleteModal';
import { generateQueryParams } from 'helpers/queryParams';
import { getStatusBadge } from './status';

const tableHead = [
  { id: 1, name: "#", isSortable: true, key: "id" },
  { id: 2, name: "Customer", isSortable: true, key: "customer_name" },
  { id: 3, name: "Phone", isSortable: false, key: "phone" },
  { id: 4, name: "Venue Name", isSortable: true, key: "venue_name" },
  { id: 5, name: "Delivery Address", isSortable: true, key: "delivery_address" },
  { id: 6, name: "Booking Date", isSortable: true, key: "booking_date" },
  { id: 7, name: "Products", isSortable: false, key: "products" },
  { id: 8, name: "Balance", isSortable: true, key: "total_amount" },
  { id: 9, name: "Invoice", isSortable: false, key: "total_amount" },
  { id: 10, name: "Status", isSortable: true, key: "booking_status" },
  { id: 11, name: "Created", isSortable: true, key: "created_at" },
  { id: 12, name: "Updated", isSortable: true, key: "updated_at" },
  { id: 13, name: "Actions", isSortable: false, key: "actions" }
];


function Bookings() {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [data, setData] = useState<any[]>([]);
  const [pagination, setPagination] = useState<any>({current_page: 1, last_page: 1, per_page: 10, total: 0});

  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const [selectedItem, setSelectedItem] = useState<any>(null);
  
  const { filters, updateFilter } = useTableFilters();

  const fetchData = async () => {
    setIsLoading(true);
    const response = await getBookings(generateQueryParams(filters));
    if (response.success) {
      setData(response.data.items);
      setPagination(response.data.pagination);
    }
    setIsLoading(false);
  }

  useEffect(() => {
    fetchData();
  }, [filters]);

  const handleDeleteClick = (item) => {
    setSelectedItem(item);
    setDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (selectedItem) {
      const response = await deleteBooking(selectedItem.id);
      if (response.success) {
        setDeleteModal(false);
        setSelectedItem(null);
        fetchData();
      }
    }
  };

  const onCloseClick = () => {
    setDeleteModal(false);
    setSelectedItem(null);
  };
  
  return (
    <>
    <div className="page-content">
      <Container fluid>
        <Breadcrumb items={[{ label: "Bookings" }]} />
        {isLoading ? <div className="position-relative vh-100"><Spinner/></div>
        :
        <Card>
          <CardBody>
            <PaginatedTable
              tableHead={tableHead}
              pagination={pagination}
              customButton={
                <div className='d-flex justify-content-end mb-3 align-items-center '>
                <Link to={"create"} className= "btn btn-primary me-2"><i className="mdi mdi-plus me-1"></i>Create Custom Booking</Link>
                <Link to={'create/bulk'} className= "btn btn-primary"><i className="mdi mdi-plus me-1"></i>Upload CSV</Link>
                </div>
              }
              updateFilter={updateFilter}
              filters={filters}
              > 
              {data.length > 0 && data.map((item) => (
                <tr key={item.id}>
                  <td>{item.id}</td>
                  <td>
                    <div className="user-info">
                      <h5>{item?.customer?.name}</h5>
                      <p>{item?.customer?.email}</p>
                    </div>
                  </td>
                  <td>{item?.customer?.phone_no_mobile}</td>
                  <td>{item?.delivery?.venue}</td>
                  <td>
                    <div className="user-info">
                      <h5>{item?.delivery?.complete_postcode}</h5>
                      <p>{item?.delivery?.town}</p>
                    </div>
                  </td>
                  <td>{convertDateFormat(item.booking_date)}</td>
                  <td>{item?.products?.length + item?.packages?.length || 0}</td>
                  <td>{Number(item?.payments?.[0]?.total_amount).toFixed(2)}</td>
                  <td>
                    <Link title="View" to={`/payments/${item.id}`}>
                      {item?.payments?.[0]?.invoice_id}
                    </Link>
                  </td>
                  <td>{getStatusBadge(item?.booking_status?.status_slug || "")}</td>
                  <td>{convertDateFormat(item.created_at)}</td>
                  <td>{convertDateFormat(item.updated_at)}</td>
                  <td>
                    <div>
                      <Link to={`${item.id}/edit`} className="btn btn-primary btn-sm me-1" title='Edit'><i className="bx bx-pencil"/></Link>
                      <Link to={`${item.id}/delivery`} className="btn btn-info btn-sm me-1" title='Delivery Document'><i className="fas fa-file-export"/></Link>
                      <Link to={`${item.id}/collection`} className="btn btn-warning btn-sm me-1" title='Collection Document'><i className="fas fa-file-download"/></Link>
                      <button className="btn btn-danger btn-sm" onClick={() => handleDeleteClick(item)} title='Delete'><i className="mdi mdi-trash-can-outline"></i></button>
                    </div>
                  </td>
                </tr>))
              }
              {!isLoading && data.length === 0 && <tr className="text-center"><td colSpan={tableHead.length} className="text-danger">No Data Found</td></tr>}
            </PaginatedTable>
          </CardBody>
        </Card>
        }
      </Container>
    </div>
    {deleteModal && 
    <DeleteModal
      show={deleteModal}
      onDeleteClick={handleDeleteConfirm}
      onCloseClick={onCloseClick}
      message={`"${selectedItem?.customer?.name}" booking`}
    />}
    </>
  );
}

export default Bookings;
